import { Card, Tag, Text, Title } from '@nimbus-ds/components'
import { cn } from '@/lib/utils'
import { rationaleRungsOf, type RationaleRung } from '@/lib/economic-calculations'
import type { Experiment, ExperimentResults } from '@/types'

/**
 * POR QUÉ — consultant layer, data-first. One row per rung of the goal ladder with the raw counts
 * and a short reading, then the decision rule that applied and (when decided) the effect size as a
 * range. Register rules: risk as plain odds ("~1 en 10"), magnitude as a range — never posterior /
 * CI / ROPE / p-value (those live one layer down, in Números).
 */

interface RationaleCardProps {
  experiment: Experiment | null
  results: ExperimentResults | null
}

const fmtCell = (c: { sessions: number; conversions: number } | null) => {
  if (!c) return '—'
  const rate = c.sessions > 0 ? ((c.conversions / c.sessions) * 100).toFixed(1) : '0.0'
  return `${c.conversions.toLocaleString('es-ES')} de ${c.sessions.toLocaleString('es-ES')} (${rate}%)`
}

const oddsText = (p: number | null) => {
  if (p == null || p <= 0) return null
  if (p < 0.001) return 'menos de 1 en 1000'
  return `~1 en ${Math.round(1 / p)}`
}

const fmtPP = (v: number) => `${v >= 0 ? '+' : '−'}${Math.abs(v * 100).toFixed(1)} pp`

const PRECISION_TEXT: Record<string, string> = {
  precise: 'estimación firme',
  approximate: 'estimación aproximada',
  direction_only: 'la dirección es firme; el tamaño, todavía no',
  unknown: '',
}

function readingOf(rung: RationaleRung): { text: string; strong: boolean } {
  if (!rung.concluded) return { text: 'Parejo — no define', strong: false }
  const who = rung.leaderIsHypothesis ? 'la hipótesis' : 'la línea base'
  const odds = oddsText(rung.pLoser)
  return { text: `Gana ${who}${odds ? ` · chance de error ${odds}` : ''}`, strong: true }
}

export function RationaleCard({ experiment, results }: RationaleCardProps) {
  const isNegative = Boolean(experiment?.is_negative || (results as any)?.is_negative)
  const rungs = rationaleRungsOf(results, isNegative)
  if (rungs.length === 0) return null

  const r = results as any
  const decidedOn = r?.decision_eta?.decided_on ?? r?.summary?.decided_on ?? null
  const guardrailState = r?.guardrail?.state ?? null
  const etaState = r?.decision_eta?.state ?? null

  // Consultant wording for the rung name (never "guardarraíl" as jargon).
  const rungLabel = (rung: RationaleRung) =>
    rung.key === 'purchase' && rungs.length > 1 ? 'Ventas (control de fondo)' : rung.label

  const decisionText = (() => {
    if (guardrailState === 'veto')
      return 'Decisión bloqueada: el objetivo y las ventas se contradicen. Lo revisa el consultor antes de aplicar.'
    if (decidedOn === 'guardrail')
      return 'Deciden las ventas — regla pre-acordada: si el objetivo no define y las ventas sí, mandan las ventas.'
    if (decidedOn === 'goal') return 'Decide el objetivo del experimento.'
    if (etaState === 'no_difference') return 'Conclusión: sin diferencia que importe. Se puede cerrar.'
    if (etaState === 'around_margin')
      return 'El efecto, si existe, es más chico de lo que la prueba considera relevante: se decide por criterio.'
    return 'Sin decisión todavía: los datos no alcanzan para definir.'
  })()

  // %-first hierarchy (same convention as Datos/Evolución): relative % is the primary unit,
  // pp rides along in parentheses. Denominator = the rung's baseline rate; pp-only fallback
  // when the baseline has no rate to divide by.
  const baseRateOf = (rung: RationaleRung) => {
    const b = rung.counts.baseline
    return b && b.sessions > 0 && b.conversions > 0 ? b.conversions / b.sessions : null
  }
  const fmtRel = (frac: number) => `${frac >= 0 ? '+' : '−'}${Math.abs(frac * 100).toFixed(0)}%`

  const deciding = rungs.find((rung) => rung.decides && rung.concluded)
  const magnitudeText = (() => {
    if (!deciding || !deciding.ci) return null
    const base = baseRateOf(deciding)
    const range = base
      ? `entre ${fmtRel(deciding.ci[0] / base)} y ${fmtRel(deciding.ci[1] / base)} (${fmtPP(deciding.ci[0])} a ${fmtPP(deciding.ci[1])})`
      : `entre ${fmtPP(deciding.ci[0])} y ${fmtPP(deciding.ci[1])}`
    return `Efecto en ${rungLabel(deciding).toLowerCase()}: ${range}${
      PRECISION_TEXT[deciding.precision] ? ` — ${PRECISION_TEXT[deciding.precision]}` : ''
    }.`
  })()

  // Bounded-downside reading: worst vs best plausible case for whoever leads the relevant rung.
  const asymRung = deciding ?? rungs.find((rung) => rung.asymmetry?.actionable)
  const asym = asymRung?.asymmetry
  const riskText = (() => {
    if (!asym || !asymRung) return null
    const who = asym.leaderIsHypothesis ? 'la hipótesis' : 'la línea base'
    const base = baseRateOf(asymRung)
    const cases = base
      ? `−${(asym.downside / base * 100).toFixed(0)}% · mejor caso: +${(asym.upside / base * 100).toFixed(0)}% (−${(
          asym.downside * 100
        ).toFixed(1)} / +${(asym.upside * 100).toFixed(1)} pp)`
      : `−${(asym.downside * 100).toFixed(1)} pp · mejor caso: +${(asym.upside * 100).toFixed(1)} pp`
    return `Peor caso plausible para ${who}: ${cases}${asym.actionable ? ' — el riesgo de aplicar es mínimo' : ''}.`
  })()

  return (
    <Card>
      <Card.Header>
        <Title as="h4">Por qué</Title>
      </Card.Header>
      <Card.Body className="space-y-3">
        <div className="overflow-x-auto">
          <div className="min-w-[560px]">
            {/* header */}
            <div className="grid grid-cols-[1.2fr_1fr_1fr_1.3fr] gap-2 border-b pb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <div>Métrica</div>
              <div className="text-right">Hipótesis</div>
              <div className="text-right">Línea base</div>
              <div className="text-right">Lectura</div>
            </div>
            {rungs.map((rung) => {
              const reading = readingOf(rung)
              return (
                <div
                  key={rung.key}
                  className="grid grid-cols-[1.2fr_1fr_1fr_1.3fr] items-baseline gap-2 border-b py-2 last:border-0"
                >
                  <div className="min-w-0 flex items-center gap-1.5">
                    <Text fontWeight={rung.decides ? 'bold' : 'medium'} color="neutral-textHigh" fontSize="caption">
                      {rungLabel(rung)}
                    </Text>
                    {rung.decides && <Tag appearance="primary">decide</Tag>}
                  </div>
                  <div className="text-right text-sm tabular-nums">{fmtCell(rung.counts.hypothesis)}</div>
                  <div className="text-right text-sm tabular-nums">{fmtCell(rung.counts.baseline)}</div>
                  <div
                    className={cn(
                      'text-right text-sm',
                      reading.strong ? 'font-medium' : 'text-muted-foreground'
                    )}
                  >
                    {reading.text}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className="space-y-1">
          <Text fontSize="caption">
            <Text as="span" fontWeight="medium" color="neutral-textHigh">
              Decisión:{' '}
            </Text>
            {decisionText}
          </Text>
          {magnitudeText && (
            <Text fontSize="caption">
              <Text as="span" fontWeight="medium" color="neutral-textHigh">
                Tamaño:{' '}
              </Text>
              {magnitudeText}
            </Text>
          )}
          {riskText && (
            <Text fontSize="caption">
              <Text as="span" fontWeight="medium" color="neutral-textHigh">
                Riesgo:{' '}
              </Text>
              {riskText}
            </Text>
          )}
        </div>
      </Card.Body>
    </Card>
  )
}
